/**
 * Catalog Auth — eager authentication and session state.
 *
 * Runs token refresh on import so the user is logged in before the catalog
 * panel is opened.  Also persists the last-opened folder to localStorage
 * so it survives full page reloads.
 *
 * Import this module early (e.g. from index.ts) to trigger the init.
 */

// ============================================================================
// Constants
// ============================================================================

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

export const LS_REFRESH_TOKEN_KEY = 'trace-catalog-refresh-token';
export const LS_TEAM_ID_KEY = 'trace-catalog-team-id';
export const LS_EMAIL_KEY = 'trace-catalog-email';

const LS_FOLDER_ID_KEY = 'trace-catalog-folder-id';
const LS_FOLDER_TITLE_KEY = 'trace-catalog-folder-title';

// ============================================================================
// Token State
// ============================================================================

let _catalogAccessToken: string | null = null;
let _accessTokenExpiresAt: number | null = null;
const _tokenListeners: Set<(token: string | null) => void> = new Set();

/** Refresh proactively when this close (ms) to expiry. */
const REFRESH_LEEWAY_MS = 60_000;

/** Parse JWT `exp` (seconds since epoch) and return ms-epoch, or null on failure. */
function parseJwtExpMs(token: string): number | null {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    try {
        const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
        const json = atob(padded);
        const claims = JSON.parse(json) as { exp?: number };
        return typeof claims.exp === 'number' ? claims.exp * 1000 : null;
    } catch {
        return null;
    }
}

/** Get the current catalog access token (null if not logged in). May be stale; prefer ensureFreshToken() before API calls. */
export function getCatalogToken(): string | null {
    return _catalogAccessToken;
}

/** Set the catalog access token (called on login/refresh). */
export function setCatalogToken(token: string | null): void {
    _catalogAccessToken = token;
    _accessTokenExpiresAt = token ? parseJwtExpMs(token) : null;
    for (const listener of _tokenListeners) {
        listener(token);
    }
}

/** Subscribe to token changes. Returns an unsubscribe function. */
export function onCatalogTokenChange(listener: (token: string | null) => void): () => void {
    _tokenListeners.add(listener);
    return () => { _tokenListeners.delete(listener); };
}

// ============================================================================
// Email State
// ============================================================================

let _catalogEmail: string | null = localStorage.getItem(LS_EMAIL_KEY);

export function getCatalogEmail(): string | null { return _catalogEmail; }

export function setCatalogEmail(email: string | null): void {
    _catalogEmail = email;
    if (email) localStorage.setItem(LS_EMAIL_KEY, email);
    else localStorage.removeItem(LS_EMAIL_KEY);
}

// ============================================================================
// Session Navigation State
// ============================================================================
// Module-level state that survives inspector teardown/rebuild (e.g. when
// switching to a different inspector side-pane and back, or when opening a
// scene from the catalog replaces the Babylon scene and the inspector remounts).

type CatalogTab = 'catalog' | 'myContent' | 'projects';

let _catalogActiveTab: CatalogTab = 'myContent';
/** Which tab was last active. Consumers MUST call this getter — direct
 * `export let` exports are snapshotted at import time and go stale. */
export function getCatalogActiveTab(): CatalogTab { return _catalogActiveTab; }
export function setCatalogActiveTab(tab: CatalogTab): void { _catalogActiveTab = tab; }

// ----------------------------------------------------------------------------
// Per-tab state snapshot store
// ----------------------------------------------------------------------------
// Keyed by an arbitrary string slot (e.g. "myContent.folders"). Used by the
// catalog panel tabs so their fetched data and UI state survive remounts.

const _catalogStateSlots: Map<string, unknown> = new Map();
export function getCatalogStateSlot<T>(key: string): T | undefined {
    return _catalogStateSlots.get(key) as T | undefined;
}
export function setCatalogStateSlot<T>(key: string, value: T): void {
    _catalogStateSlots.set(key, value);
}
/** Clear all cached tab state. Called on logout. */
export function clearCatalogStateSlots(): void {
    _catalogStateSlots.clear();
}

// ----------------------------------------------------------------------------
// Shared team selection
// ----------------------------------------------------------------------------
// The active team is shared across all tabs (My Content, Projects) so changing
// it from the panel header re-scopes every tab's view. Persisted to
// localStorage as before so it survives page reloads.

let _catalogSelectedTeamId: string | null = localStorage.getItem(LS_TEAM_ID_KEY);
const _teamListeners: Set<(teamId: string | null) => void> = new Set();

export function getCatalogSelectedTeamId(): string | null { return _catalogSelectedTeamId; }

export function setCatalogSelectedTeamId(teamId: string | null): void {
    if (_catalogSelectedTeamId === teamId) return;
    _catalogSelectedTeamId = teamId;
    if (teamId) localStorage.setItem(LS_TEAM_ID_KEY, teamId);
    else localStorage.removeItem(LS_TEAM_ID_KEY);
    for (const listener of _teamListeners) listener(teamId);
}

export function onCatalogSelectedTeamIdChange(listener: (teamId: string | null) => void): () => void {
    _teamListeners.add(listener);
    return () => { _teamListeners.delete(listener); };
}

let _catalogSelectedFolderId: string | null = localStorage.getItem(LS_FOLDER_ID_KEY);
let _catalogSelectedFolderTitle: string | null = localStorage.getItem(LS_FOLDER_TITLE_KEY);
/** The folder the user was browsing in My Content (null = folder list). Persisted to localStorage. */
export function getCatalogSelectedFolderId(): string | null { return _catalogSelectedFolderId; }
export function getCatalogSelectedFolderTitle(): string | null { return _catalogSelectedFolderTitle; }

export function setCatalogSelectedFolder(id: string | null, title: string | null): void {
    _catalogSelectedFolderId = id;
    _catalogSelectedFolderTitle = title;
    if (id) localStorage.setItem(LS_FOLDER_ID_KEY, id);
    else localStorage.removeItem(LS_FOLDER_ID_KEY);
    if (title) localStorage.setItem(LS_FOLDER_TITLE_KEY, title);
    else localStorage.removeItem(LS_FOLDER_TITLE_KEY);
}

// ============================================================================
// Token Refresh
// ============================================================================

/** Try to get a new access token using a stored refresh token. */
export async function tryRefreshToken(): Promise<string | null> {
    const refreshToken = localStorage.getItem(LS_REFRESH_TOKEN_KEY);
    if (!refreshToken || !API_BASE_URL) return null;
    try {
        const formData = new FormData();
        formData.append('refreshToken', refreshToken);
        const res = await fetch(`${API_BASE_URL}/auth/token/refresh`, {
            method: 'POST',
            body: formData,
        });
        if (!res.ok) {
            localStorage.removeItem(LS_REFRESH_TOKEN_KEY);
            return null;
        }
        const data = await res.json() as { access_token?: string; accessToken?: string; refresh_token?: string; refreshToken?: string };
        const newRefresh = data.refresh_token ?? data.refreshToken;
        if (newRefresh) localStorage.setItem(LS_REFRESH_TOKEN_KEY, newRefresh);
        return data.access_token ?? data.accessToken ?? null;
    } catch {
        return null;
    }
}

// ============================================================================
// Proactive Refresh — used by apiConfig() before every authenticated request
// ============================================================================

let _refreshInFlight: Promise<string | null> | null = null;

/**
 * Returns a valid access token, refreshing proactively if the current one is
 * near expiry. Returns null if refresh fails (caller should redirect to login).
 *
 * Concurrent calls share a single in-flight refresh promise.
 */
export async function ensureFreshToken(): Promise<string | null> {
    const now = Date.now();
    const expiresAt = _accessTokenExpiresAt;
    const needsRefresh = !_catalogAccessToken
        || expiresAt === null
        || expiresAt - now < REFRESH_LEEWAY_MS;

    if (!needsRefresh) return _catalogAccessToken;

    // If we have no refresh token, there's nothing to do — treat as logged out.
    if (!localStorage.getItem(LS_REFRESH_TOKEN_KEY)) {
        if (_catalogAccessToken) onRefreshFailed();
        return null;
    }

    if (!_refreshInFlight) {
        _refreshInFlight = (async () => {
            try {
                const fresh = await tryRefreshToken();
                if (fresh) {
                    const email = _catalogEmail ?? localStorage.getItem(LS_EMAIL_KEY) ?? '';
                    handleLoginSuccess(fresh, email);
                    return fresh;
                }
                onRefreshFailed();
                return null;
            } finally {
                _refreshInFlight = null;
            }
        })();
    }
    return _refreshInFlight;
}

/** Toast + clear session when a mid-session refresh fails. Idempotent. */
function onRefreshFailed(): void {
    const wasLoggedIn = _catalogAccessToken !== null;
    localStorage.removeItem(LS_REFRESH_TOKEN_KEY);
    setCatalogToken(null);
    setCatalogEmail(null);
    clearCatalogStateSlots();
    if (wasLoggedIn) {
        // Lazy import avoids a hard module dep cycle if toast ever grows imports.
        void import('./toast').then(({ showToast }) => {
            showToast('Session expired. Please sign in again.', 'error');
        });
    }
}

// ============================================================================
// Login / Logout helpers
// ============================================================================

export function handleLoginSuccess(accessToken: string, email: string): void {
    setCatalogToken(accessToken);
    setCatalogEmail(email);
}

export function handleLogout(): void {
    localStorage.removeItem(LS_REFRESH_TOKEN_KEY);
    setCatalogToken(null);
    setCatalogEmail(null);
    clearCatalogStateSlots();
}

// ============================================================================
// Eager Init — runs on import
// ============================================================================

/** Promise that resolves when initial token refresh completes. */
export const authReady: Promise<void> = (async () => {
    const accessToken = await tryRefreshToken();
    if (accessToken) {
        const storedEmail = localStorage.getItem(LS_EMAIL_KEY) ?? '';
        handleLoginSuccess(accessToken, storedEmail);
    }
})();
